// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { xIsAuthenticated } from "@tps/App/Store/Selectors/GeneralSelectors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        fullName: state.components.contactUsForm.fieldValues["fullName"],
        // xng-change
        isAuthenticated: xIsAuthenticated(state),
        firstName: session.firstName,
        lastName: session.lastName,
    };
};

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FullNameStyles {
    fullNameTextField?: TextFieldPresentationProps;
}

export const fullNameStyles: FullNameStyles = {};

const styles = fullNameStyles;

const FullName: React.FC<Props> = ({ fields, fullName, setFieldValue, isAuthenticated, firstName, lastName }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [fullNameErrorMessage, setFullNameErrorMessage] = React.useState("");

    // xng-change: Prefill fullName with logged-in user's first name and last name
    React.useEffect(() => {
        if (isAuthenticated && firstName) {
            const userFullName = `${firstName} ${lastName}`.trim();
            setFieldValue({ key: "fullName", value: userFullName });
        }
    }, [isAuthenticated, firstName, lastName, setFieldValue]);

    React.useEffect(() => {
        contactFormContext.validators.fullName = validateFullName;
        if (typeof fullName !== "undefined") {
            validateFullName();
        }
    }, [fullName]);

    React.useEffect(() => {
        contactFormContext.validators.fullName = validateFullName;
    }, [isRequired]);

    const validateFullName = () => {
        const errorMessage = isRequired && !fullName ? (siteMessage("Field_Required", label) as string) : "";
        setFullNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const fullNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "fullName", value: event.target.value });
    };

    return (
        <TextField
            {...styles.fullNameTextField}
            label={label}
            placeholder={hintText}
            required={isRequired}
            value={fullName || ""}
            error={fullNameErrorMessage}
            onChange={fullNameChangeHandler}
            type="fullName"
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FullName),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Full Name",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Full Name",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
